import { format } from 'date-fns'

import { IOrg, IUser, useMst } from '#state'

import { useSeatCapacity } from '../Members/useSeatCapacity'
import { useSeatChanges } from '../Members/useSeatChanges'
import { BillingPlan, isLiveSubscription, STRIPE_TIER } from '../Org/plans'
import { usePortal, useWorkspaceBillingPlan } from '../Org/useBillingPlan'

import { WorkspaceSeatData } from './types'

const dateLabel = (date: Date | null): string | undefined =>
  date ? format(date, 'MMMM d, yyyy') : undefined

export const workspaceSeatData = ({
  plan,
  capacity,
  org,
  user,
}: {
  plan: BillingPlan | null | undefined
  capacity: ReturnType<typeof useSeatCapacity>['data']
  org: IOrg
  user: IUser
}): WorkspaceSeatData | null => {
  if (
    org.tier !== STRIPE_TIER ||
    !plan?.seats ||
    !plan.seatMode ||
    !isLiveSubscription(plan)
  ) {
    return null
  }

  const seatMode = plan.seatMode
  const headcount = capacity?.headcount ?? plan.usedSeats ?? org.members.length
  const openInvites = capacity?.openInvites ?? org.openInvites.length
  const licensed = plan.seats
  const used = seatMode === 'static' ? headcount + openInvites : headcount
  const periodEnd = dateLabel(plan.currentPeriodEnd)

  return {
    seatMode,
    seats: {
      licensed,
      occupied: headcount,
      reserved: openInvites,
      available: Math.max(0, licensed - used),
      planMax: plan.seatMax,
    },
    canChangeSeats: user.isCurrentOrgAdmin,
    canManagePlan: user.isCurrentOrgOwner,
    renewalLabel: periodEnd,
    cancelScheduled: plan.cancelAt !== null && !plan.promotional,
    scheduledSeats: plan.scheduledSeats,
    scheduledLabel: dateLabel(plan.scheduledSeatsAt),
  }
}

export const useSeats = (org: IOrg) => {
  const { user } = useMst()
  const isStripeTier = org.tier === STRIPE_TIER
  const planQuery = useWorkspaceBillingPlan(org.id, { enabled: isStripeTier })
  const capacityQuery = useSeatCapacity(org, {
    enabled: user.isCurrentOrgAdmin,
  })
  const plan = planQuery.data
  const capacity = capacityQuery.data
  const changes = useSeatChanges(org)
  const { openPortal } = usePortal()

  const workspace = workspaceSeatData({ plan, capacity, org, user })

  return {
    pending:
      (isStripeTier && planQuery.isPending) ||
      (user.isCurrentOrgAdmin && capacityQuery.pending),
    workspace,
    changes,
    managePlan: () => openPortal(),
  }
}
